import * as React from 'react';

import {
  enumOperationType,
  OperationType,
} from '@rematter/frontend-lib-api-client';
import { Button, Loading, Menu } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideDownload,
  LucideEllipsis,
  LucideFileDown,
  LucideFiles,
  LucideHistory,
  LucideMail,
} from 'lucide-react';

import {
  defaultPriceListAddItemPricingAtom,
  defaultPriceListBatchUpdateItemPricingsAtom,
  defaultPriceListDeleteItemPricingAtom,
} from '../../_graphql/mutationDefaultPriceListOps';
import { generatePriceListDocumentAtom } from '../../_graphql/mutationPriceListDocument';
import * as state from '../../_state/defaultPriceListDetail.state';
import * as variablesState from '../../_state/variablesList.state';
import { useTranslations } from '../../i18n/useTranslations';
import type {
  AddItemPricingInput,
  BatchSaveItem,
} from '../_shared/hooks/useEditablePricingTable';
import { PriceListEmailDialog } from '../_shared/PriceListEmailDialog';
import { PriceListHistoryDialog } from '../_shared/PriceListHistoryDialog';
import { PriceListView } from '../_shared/PriceListView';
import { CustomPriceListDraftDialog } from '../custom-price-lists/CustomPriceListDraftDialog';
import { ImportFromPriceListDialog } from '../custom-price-lists/ImportFromPriceListDialog';

import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { useFileDownload } from '~/shared/hooks/useFileDownload';
import { useUserDefaultFacilityId } from '~/shared/hooks/useUserFacilities';

//
// ⚛️ ⚛️ ⚛️ DEFAULT PRICE LIST VIEW ⚛️ ⚛️ ⚛️
//
export const DefaultPriceListView = () => {
  // Translations
  const t = useTranslations();

  // States
  const [isEmailDialogOpen, setIsEmailDialogOpen] = React.useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
  const [isImportFromOpen, setIsImportFromOpen] = React.useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = React.useState(false);

  // Atoms
  const [facilityId, setFacilityId] = useAtom(state.$selectedFacilityId);
  const [operationType, setOperationType] = useAtom(
    state.$selectedOperationType,
  );
  const [currency, setCurrency] = useAtom(state.$selectedCurrency);
  const [selectedCell, setSelectedCell] = useAtom(state.$selectedCell);
  const [asOfDateTime, setAsOfDateTime] = useAtom(state.$asOfDateTime);
  const canLoadPriceList = useAtomValue(state.$canLoadPriceList);
  const isLoading = useAtomValue(state.$isDefaultPriceListLoading);
  const { data: priceList } = useAtomValue(state.$remoteDefaultPriceList);
  const {
    data: itemPricings,
    isFetching: pricingsFetching,
    refetch: refetchItemPricings,
  } = useAtomValue(state.$remoteProductPricings);
  const variables = useAtomValue(variablesState.$allVariablesForAutocomplete);
  const { refetch: refetchEvaluatedFormulas } = useAtomValue(
    state.$remoteEvaluatedFormulas,
  );
  const evaluatedPrices = useAtomValue(state.$evaluatedPrices);
  const addItemPricing = useSetAtom(defaultPriceListAddItemPricingAtom({}));
  const deleteItemPricing = useSetAtom(
    defaultPriceListDeleteItemPricingAtom({}),
  );
  const batchUpdateItemPricings = useSetAtom(
    defaultPriceListBatchUpdateItemPricingsAtom({}),
  );
  const generateDocumentAction = useSetAtom(generatePriceListDocumentAtom({}));

  // Data hooks
  const toast = useToast();
  const userDefaultFacilityId = useUserDefaultFacilityId();
  const { downloadFile } = useFileDownload();

  // Memoized values
  const pricingsList = React.useMemo(() => itemPricings ?? [], [itemPricings]);

  // Callbacks
  const handleBatchSave = React.useCallback(
    async (params: { listId: string; items: BatchSaveItem[] }) => {
      await batchUpdateItemPricings({
        priceListId: params.listId,
        items: params.items,
      });
    },
    [batchUpdateItemPricings],
  );

  const handleAddItemPricing = React.useCallback(
    async (input: AddItemPricingInput) => {
      if (!priceList?.id) return;
      await addItemPricing({
        priceListId: priceList.id,
        itemId: input.itemId,
        pricingUnit: input.pricingUnit,
        position: input.position,
        tiers: input.tiers,
      });
    },
    [priceList?.id, addItemPricing],
  );

  const handleDownloadPdf = React.useCallback(async () => {
    if (!priceList?.id) return;
    try {
      const result = await generateDocumentAction({
        priceListId: priceList.id,
        priceListType: 'default',
      });
      const fileId = result?.pricingGeneratePriceListDocument?.fileId;
      if (fileId) {
        await downloadFile({
          id: fileId,
          fileName: `default-price-list.pdf`,
        });
      }
      toast.success(t.pricing.customPriceLists.toast.pdfGenerated);
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.pdfError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [priceList?.id, generateDocumentAction, downloadFile, toast, t]);

  // Effects
  React.useEffect(() => {
    if (!facilityId && userDefaultFacilityId) {
      setFacilityId(userDefaultFacilityId);
    }
  }, [facilityId, userDefaultFacilityId, setFacilityId]);

  const actionsMenu = priceList ? (
    <Menu
      trigger={
        <Button
          variant="outline"
          color="neutral"
          icon={<LucideEllipsis size={16} />}
        >
          {t.pricing.customPriceLists.detail.actions.actionsMenu}
        </Button>
      }
      items={[
        {
          label: t.pricing.customPriceLists.detail.actions.importFrom,
          onAction: () => setIsImportFromOpen(true),
          icon: <LucideDownload size={16} />,
        },
        'separator',
        {
          label: t.pricing.customPriceLists.detail.actions.viewHistory,
          onAction: () => setIsHistoryOpen(true),
          icon: <LucideHistory size={16} />,
        },
        {
          label: t.pricing.customPriceLists.detail.actions.downloadPdf,
          onAction: handleDownloadPdf,
          icon: <LucideFileDown size={16} />,
        },
        {
          label: t.pricing.customPriceLists.detail.actions.sendEmail,
          onAction: () => setIsEmailDialogOpen(true),
          icon: <LucideMail size={16} />,
        },
        'separator',
        {
          label: t.pricing.customPriceLists.detail.actions.duplicate,
          onAction: () => setIsDuplicateOpen(true),
          icon: <LucideFiles size={16} />,
        },
      ]}
    />
  ) : null;

  const toolbarLeft = (
    <>
      <SelectEnum
        enumValue={enumOperationType}
        value={operationType ?? undefined}
        onChange={(value) => setOperationType((value ?? '') as OperationType)}
        className="w-36"
      />
      <SelectFacility
        value={facilityId ?? undefined}
        onChange={(value) => setFacilityId(value ?? '')}
        placeholder={t.pricing.defaultPriceList.selectors.selectFacility}
        className="w-44"
      />
      <SelectCurrency
        value={currency ?? undefined}
        onChange={(value) => setCurrency(value ?? 'USD')}
        placeholder={t.pricing.defaultPriceList.selectors.currencyPlaceholder}
        className="w-44"
      />
    </>
  );

  return (
    <div className="@DefaultPriceListView flex h-full flex-col gap-2 p-4">
      {!canLoadPriceList && (
        <>
          <div className="flex items-center gap-2">{toolbarLeft}</div>
          <div className="py-8 text-center text-sm text-neutral/60">
            {t.pricing.defaultPriceList.prompt}
          </div>
        </>
      )}

      {canLoadPriceList && isLoading && (
        <>
          <div className="flex items-center gap-2">{toolbarLeft}</div>
          <div className="space-y-4">
            <Loading />
          </div>
        </>
      )}

      {canLoadPriceList && !isLoading && priceList && (
        <>
          <PriceListView
            toolbarLeft={toolbarLeft}
            toolbarRight={actionsMenu}
            priceListId={priceList.id}
            itemPricings={pricingsList}
            pricingsFetching={pricingsFetching}
            variables={variables}
            evaluatedPrices={evaluatedPrices}
            selectedCell={selectedCell}
            setSelectedCell={setSelectedCell}
            onBatchSave={handleBatchSave}
            onAddItemPricing={handleAddItemPricing}
            onDeleteItemPricing={deleteItemPricing}
            onRefetchPricings={refetchItemPricings}
            onRefetchEvaluated={refetchEvaluatedFormulas}
            asOfDateTime={asOfDateTime}
            onAsOfDateTimeChange={setAsOfDateTime}
            emptyTitle={t.pricing.defaultPriceList.productPricings.empty.title}
            emptyDescription={
              t.pricing.defaultPriceList.productPricings.empty.description
            }
            gridAriaLabel={t.pricing.defaultPriceList.productPricings.title}
          />
          <PriceListHistoryDialog
            isOpen={isHistoryOpen}
            onClose={() => setIsHistoryOpen(false)}
            priceListId={priceList.id}
            priceListName={`Default - ${operationType}`}
            priceListType="default"
          />
          <PriceListEmailDialog
            isOpen={isEmailDialogOpen}
            onClose={() => setIsEmailDialogOpen(false)}
            priceListId={priceList.id}
            priceListType="default"
            priceListName={`Default - ${operationType}`}
          />
          <CustomPriceListDraftDialog
            isOpen={isDuplicateOpen}
            onClose={() => setIsDuplicateOpen(false)}
            copyFromDefaultPriceListId={priceList.id}
          />
          <ImportFromPriceListDialog
            isOpen={isImportFromOpen}
            onClose={() => setIsImportFromOpen(false)}
            targetDefaultPriceListId={priceList.id}
            onSuccess={() => {
              refetchItemPricings();
              refetchEvaluatedFormulas();
            }}
          />
        </>
      )}
    </div>
  );
};
